class CartNotification extends HTMLElement {
  constructor() {
    super();

    this.notification = document.getElementById('cart-notification');
    this.header = document.querySelector('sticky-header');
    this.onBodyClick = this.handleBodyClick.bind(this);

    this.notification.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelectorAll('button[type="button"]').forEach((closeButton) =>
      closeButton.addEventListener('click', this.close.bind(this))
    );
    this.addEventListener('change', (event) => {
      if (event.target.matches('[data-notification-quantity]')) this.updateQuantity(event.target);
    });
    this.addEventListener('click', (event) => {
      if (this.updatingQuantity && event.target.closest('.cart-notification__links a, .cart-notification__links button[name="checkout"]')) {
        event.preventDefault();
      }
    });
  }

  open() {
    this.notification.classList.add('animate', 'active');

    this.notification.addEventListener(
      'transitionend',
      () => {
        this.notification.focus();
        trapFocus(this.notification);
      },
      { once: true }
    );

    document.body.addEventListener('click', this.onBodyClick);

    this.dispatchCartViewEvent();
  }

  // The notification's outer element is server-rendered once at page load, so
  // its `cart` Liquid object reflects the pre-add state. The morphed children
  // (inserted from the /cart/add.js sections response in renderContents) are
  // post-add, but they don't expose the full cart shape we need for the event
  // payload. So we keep an explicit /cart.json fetch on open. Migrating to the
  // factory + filter would require re-rendering the notification element
  // itself in sections, which is out of scope for this PR.
  async dispatchCartViewEvent() {
    const { CartViewEvent } = window.StandardEvents || {};
    if (!CartViewEvent) return;

    try {
      const response = await fetch(`${routes.cart_url}.json`);
      const cart = await response.json();
      if (!cart?.currency) return;

      this.dispatchEvent(
        new CartViewEvent({
          context: 'dialog',
          cart: CartViewEvent.createCartFromAjaxResponse(cart),
        })
      );
    } catch (e) {
      // cart:view is informational; swallow fetch errors
    }
  }

  close() {
    this.notification.classList.remove('active');
    document.body.removeEventListener('click', this.onBodyClick);

    removeTrapFocus(this.activeElement);
  }

  renderContents(parsedState, shouldOpen = true) {
    this.cartItemKey = parsedState.key;
    const contents = this.getSectionsToRender().map((section) => ({
      id: section.id,
      html: this.getSectionInnerHTML(parsedState.sections?.[section.id], section.selector),
    }));
    if (contents.some((section) => section.html === null)) return false;
    contents.forEach((section) => {
      document.getElementById(section.id).innerHTML = section.html;
    });

    if (shouldOpen) {
      if (this.header) this.header.reveal();
      this.open();
    }
    return true;
  }

  async updateQuantity(input) {
    if (this.updatingQuantity || input.disabled) return;
    if (!input.checkValidity()) {
      input.reportValidity();
      input.value = input.defaultValue;
      return;
    }
    const quantity = Number(input.value);
    if (!Number.isInteger(quantity) || quantity < 1) {
      input.value = input.defaultValue;
      return;
    }
    if (quantity === Number(input.defaultValue)) return;
    const focusedControl = document.activeElement?.closest('.mayura-notification-quantity');
    const focusName = focusedControl ? document.activeElement.name : null;
    const controls = Array.from(this.querySelectorAll('.mayura-notification-quantity button, [data-notification-quantity]'));
    const disabledStates = controls.map((control) => control.disabled);
    const error = this.querySelector('[data-notification-quantity-error]');
    if (error) { error.hidden = true; error.textContent = ''; }
    this.updatingQuantity = true;
    this.notification.setAttribute('aria-busy', 'true');
    controls.forEach((control) => { control.disabled = true; });
    try {
      const response = await fetch(routes.cart_change_url, {
        ...fetchConfig(),
        body: JSON.stringify({
          id: input.dataset.lineKey,
          quantity,
          sections: this.getSectionsToRender().map((section) => section.id),
          sections_url: window.location.pathname,
        }),
      });
      const state = await response.json();
      if (!response.ok || state.errors || state.status) {
        throw new Error(typeof state.errors === 'string' ? state.errors : state.description || window.cartStrings.error);
      }
      const item = state.items?.[Number(input.dataset.lineIndex) - 1];
      if (!item || String(item.variant_id) !== input.dataset.variantId || !this.renderContents({ ...state, key: item.key }, false)) {
        window.location.assign(routes.cart_url);
        return;
      }
      if (item.quantity !== quantity) {
        const updatedError = this.querySelector('[data-notification-quantity-error]');
        updatedError.textContent = window.cartStrings.quantityError.replace('[quantity]', item.quantity);
        updatedError.hidden = false;
      }
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-notification',
        cartData: state,
        variantId: item.variant_id,
      }).catch(console.error);
      if (focusName && this.notification.classList.contains('active')) {
        this.querySelector(`.mayura-notification-quantity [name="${focusName}"]`)?.focus();
      }
    } catch (failure) {
      input.value = input.defaultValue;
      if (error) { error.textContent = failure.message || window.cartStrings.error; error.hidden = false; }
    } finally {
      this.updatingQuantity = false;
      this.notification.removeAttribute('aria-busy');
      controls.forEach((control, index) => {
        if (control.isConnected) control.disabled = disabledStates[index];
      });
      if (input.isConnected) input.closest('quantity-input')?.validateQtyRules();
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-notification-product',
        selector: `[id="cart-notification-product-${this.cartItemKey}"]`,
      },
      {
        id: 'cart-notification-button',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    if (typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector)?.innerHTML ?? null;
  }

  handleBodyClick(evt) {
    const target = evt.target;
    if (target !== this.notification && !target.closest('cart-notification')) {
      const disclosure = target.closest('details-disclosure, header-menu');
      this.activeElement = disclosure ? disclosure.querySelector('summary') : null;
      this.close();
    }
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-notification', CartNotification);
